import { useState } from 'react'
import {
  Table, Group, TextInput, Title, Pagination, Badge, ActionIcon,
  Card, Text, Modal, Stack, Textarea, Button, Select, Alert, Skeleton,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { IconSearch, IconEdit, IconTrash, IconPlus, IconAlertCircle, IconRefresh } from '@tabler/icons-react'
import { useForm, Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { useCustomers } from '@/lib/swr'
import { GENDER_OPTIONS } from '@/constants'
import type { Customer } from '@/types'
import dayjs from 'dayjs'

interface CustomerFormData {
  firstName: string
  lastName: string
  email: string
  phone: string
  dateOfBirth: Date | null
  gender: string | null
  address: string
  notes: string
}

const emptyForm: CustomerFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  dateOfBirth: null,
  gender: null,
  address: '',
  notes: '',
}

export default function CustomerList() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CustomerFormData>({ defaultValues: emptyForm })

  const queryParams: Record<string, string> = { page: String(page), limit: '20' }
  if (search) queryParams.search = search

  const { data: customersRes, isLoading: loading, error: swrError, mutate: reloadCustomers } = useCustomers(queryParams)
  const customers: Customer[] = (customersRes as any)?.data ?? []
  const pagination = {
    page,
    pages: (customersRes as any)?.pagination?.totalPages || (customersRes as any)?.pagination?.pages || 1,
    total: (customersRes as any)?.pagination?.total || 0,
  }
  const error = swrError ? (swrError instanceof Error ? swrError.message : 'Үйлчлүүлэгчдийг ачаалахад алдаа гарлаа') : null

  const load = () => { reloadCustomers() }

  const openCreate = () => {
    setEditingId(null)
    reset(emptyForm)
    setModalOpen(true)
  }

  const openEdit = (customer: Customer) => {
    setEditingId(customer._id)
    reset({
      firstName: customer.firstName,
      lastName: customer.lastName,
      email: customer.email || '',
      phone: customer.phone,
      dateOfBirth: customer.dateOfBirth ? new Date(customer.dateOfBirth) : null,
      gender: customer.gender || null,
      address: customer.address || '',
      notes: customer.notes || '',
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    reset(emptyForm)
  }

  const onSubmit = handleSubmit(async (form) => {
    try {
      const data: Record<string, unknown> = {
        firstName: form.firstName,
        lastName: form.lastName || undefined,
        email: form.email || undefined,
        phone: form.phone,
        dateOfBirth: form.dateOfBirth ? dayjs(form.dateOfBirth).format('YYYY-MM-DD') : undefined,
        gender: form.gender || undefined,
        address: form.address || undefined,
        notes: form.notes || undefined,
      }

      if (editingId) {
        await api.admin.updateCustomer(editingId, data)
        toast.success('Үйлчлүүлэгч шинэчлэгдлээ')
      } else {
        await api.admin.createCustomer(data)
        toast.success('Үйлчлүүлэгч үүсгэгдлээ')
      }
      closeModal()
      load()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.admin.deleteCustomer(deleteId)
      toast.success('Үйлчлүүлэгч устгагдлаа')
      load()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Устгахад алдаа гарлаа')
    } finally {
      setDeleting(false)
      setDeleteId(null)
    }
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          <Title order={2}>Үйлчлүүлэгчид</Title>
          {pagination.total > 0 && <Badge variant="light" color="gray">{pagination.total}</Badge>}
        </Group>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
          Нэмэх
        </Button>
      </Group>

      <Group mb="md">
        <TextInput
          placeholder="Хайх..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => { setSearch(e?.currentTarget?.value ?? ''); setPage(1) }}
          w={300}
        />
      </Group>

      {error && (
        <Alert
          icon={<IconAlertCircle size={18} />}
          title="Алдаа"
          color="red"
          variant="light"
          mb="md"
        >
          <Group justify="space-between" align="center">
            <Text size="sm">{error}</Text>
            <Button size="xs" variant="light" color="red" leftSection={<IconRefresh size={14} />} onClick={load}>
              Дахин оролдох
            </Button>
          </Group>
        </Alert>
      )}

      <Card shadow="xs" padding={0} radius="md" withBorder>
        <Table.ScrollContainer minWidth={700}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr style={{ backgroundColor: 'var(--app-table-header)' }}>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Нэр</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Утас</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Имэйл</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Хүйс</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Захиалга</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Сүүлийн ирсэн</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үйлдэл</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Table.Tr key={i}>
                    <Table.Td><Skeleton h={16} w={24} /></Table.Td>
                    <Table.Td><Skeleton h={16} /></Table.Td>
                    <Table.Td><Skeleton h={16} w={90} /></Table.Td>
                    <Table.Td><Skeleton h={16} /></Table.Td>
                    <Table.Td><Skeleton h={20} w={60} radius="xl" /></Table.Td>
                    <Table.Td><Skeleton h={16} w={30} /></Table.Td>
                    <Table.Td><Skeleton h={16} w={90} /></Table.Td>
                    <Table.Td><Skeleton h={28} w={60} radius="md" /></Table.Td>
                  </Table.Tr>
                ))
              ) : (
                <>
                  {customers.map((customer, i) => {
                    const genderLabel = GENDER_OPTIONS.find((g) => g.value === customer.gender)?.label
                    return (
                      <Table.Tr key={customer._id}>
                        <Table.Td c="dimmed" fz="sm">{(pagination.page - 1) * 20 + i + 1}</Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {customer.lastName ? `${customer.lastName.charAt(0)}.` : ''}{customer.firstName}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{customer.phone}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">{customer.email || '-'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" color={customer.gender === 'male' ? 'blue' : customer.gender === 'female' ? 'pink' : 'gray'} size="sm">
                            {genderLabel || '-'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={500}>{customer.totalBookings || 0}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {customer.lastVisit ? dayjs(customer.lastVisit).format('YYYY.MM.DD') : '-'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={6}>
                            <ActionIcon variant="light" color="blue" radius="md" onClick={() => openEdit(customer)}>
                              <IconEdit size={16} />
                            </ActionIcon>
                            <ActionIcon variant="light" color="red" radius="md" onClick={() => setDeleteId(customer._id)}>
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                  {!loading && !error && customers.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={8}>
                        <Text ta="center" c="dimmed" py="xl">Үйлчлүүлэгч олдсонгүй</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        {pagination.pages > 1 && (
          <Group justify="center" p="md" style={{ borderTop: '1px solid var(--app-border)' }}>
            <Pagination total={pagination.pages} value={pagination.page} onChange={(p) => setPage(p)} />
          </Group>
        )}
      </Card>

      {/* Delete Confirmation */}
      <Modal
        opened={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Устгах"
        centered
        size="sm"
      >
        <Stack>
          <Text size="sm">Энэ үйлчлүүлэгчийг устгахдаа итгэлтэй байна уу?</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteId(null)}>Болих</Button>
            <Button color="red" onClick={handleDelete} loading={deleting} disabled={deleting}>Устгах</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Create / Edit Modal */}
      <Modal
        opened={modalOpen}
        onClose={closeModal}
        title={editingId ? 'Үйлчлүүлэгч засах' : 'Шинэ үйлчлүүлэгч'}
        centered
        size="lg"
      >
        <form onSubmit={onSubmit}>
          <Stack>
            <Group grow>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextInput label="Овог" placeholder="Овог" {...field} />
                )}
              />
              <Controller
                name="firstName"
                control={control}
                rules={{ validate: (v) => (v.trim() ? true : 'Нэр шаардлагатай') }}
                render={({ field }) => (
                  <TextInput label="Нэр *" placeholder="Нэр" {...field} required error={errors.firstName?.message} />
                )}
              />
            </Group>
            <Group grow>
              <Controller
                name="phone"
                control={control}
                rules={{ validate: (v) => (v.trim() ? true : 'Утас шаардлагатай') }}
                render={({ field }) => (
                  <TextInput label="Утас *" placeholder="99001122" {...field} required error={errors.phone?.message} />
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextInput label="Имэйл" placeholder="email@example.com" {...field} />
                )}
              />
            </Group>
            <Group grow>
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Хүйс"
                    placeholder="Сонгох"
                    data={GENDER_OPTIONS}
                    value={field.value}
                    onChange={(v) => field.onChange(v)}
                    clearable
                  />
                )}
              />
              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field }) => (
                  <DateInput
                    label="Төрсөн огноо"
                    placeholder="Огноо сонгох"
                    value={field.value}
                    onChange={(v) => field.onChange(v as Date | null)}
                    clearable
                  />
                )}
              />
            </Group>
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <TextInput label="Хаяг" placeholder="Хаяг" {...field} />
              )}
            />
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <Textarea label="Тэмдэглэл" placeholder="Нэмэлт тэмдэглэл..." autosize minRows={2} {...field} />
              )}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={closeModal} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {editingId ? 'Хадгалах' : 'Үүсгэх'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </div>
  )
}
